import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Sparkles, LayoutDashboard, Wallet, Store, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login gagal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1620112568215-6e4ec766e7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMHByb2Zlc3Npb25hbCUyMGJsdWUlMjBncmVlbiUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4ODA5MTM4Nnww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-slate-900/40 to-emerald-700/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Store className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight">Kelola Usaha</span>
          </div>
          <div className="max-w-md space-y-4">
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Rapi keuangan, tenang operasional.
            </h1>
            <p className="text-slate-200 text-base">
              Kelola pendapatan, pengeluaran, dan anggaran multi-cabang dalam satu dashboard yang jernih.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-start px-6 sm:px-12 lg:px-24 py-16">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-lg bg-emerald-500 flex items-center justify-center text-white">
              <Store className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-slate-900">Kelola Usaha</span>
          </div>

          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> Manajemen Keuangan Multi-Cabang
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              Masuk untuk melanjutkan
            </h2>
            <p className="text-slate-500 text-base">
              Gunakan email dan sandi akun Anda.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm text-slate-700">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="nama@domain.com" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm text-slate-700">Sandi</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} required value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                       className="pr-10" data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                        data-testid="login-password-toggle" tabIndex={-1} aria-label="Lihat sandi">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={submitting} data-testid="login-submit"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white text-base font-semibold rounded-lg transition-all hover:-translate-y-0.5"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
              {submitting ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <div className="grid grid-cols-3 gap-3 pt-2">
            {[
              { icon: LayoutDashboard, label: "Dashboard" },
              { icon: Wallet, label: "Anggaran" },
              { icon: Sparkles, label: "Multi-Cabang" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 text-center">
                <Icon className="h-4 w-4 mx-auto mb-2 text-emerald-600" />
                <div className="text-xs text-slate-600 font-medium">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
